import React, { useEffect, useState } from 'react';
import { Spin, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { NavLink, useNavigate } from 'react-router-dom';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styles from './index.module.scss';
import { IconFileText } from '../../../Common/Icon';
import { useProductContext } from '../../../../context/products';
import { Article, useArticlesGet } from '../../../../hooks/api/article';
import { formatDate } from '../../../../utils';

export default function TopArticles(): React.ReactNode | null {
  const { data, loading, error } = useProductContext();
  const navigate = useNavigate();
  const products = data ?? [];
  const articlesGet = useArticlesGet({ params: { pageSize: '100', status: 'published' } });

  const [activeTab, setActiveTab] = useState<string>('');

  useEffect(() => {
    if (products.length > 0 && !activeTab) {
      setActiveTab(products[0].id);
    }
  }, [products, activeTab]);

  const onChange = (key: string) => {
    setActiveTab(key);
  };

  const renderTabItems = (productId: string) => {
    const filtered = articlesGet.data?.data
      .filter((article: Article) => article.product.id === productId && article.status === 'published')
      .slice(0, 7);

    return filtered?.map((item: Article) => (
      <div
        role="button"
        tabIndex={-1}
        className={styles.item}
        key={item.id}
        onClick={() => {
          navigate(`/article/${item.product.name}/${item.product.id}/${item.id}`);
        }}
      >
        <div className={styles.icon}>
          <IconFileText color="currentColor" />
        </div>
        <div className={styles.info}>
          <div className={styles.infoContent}>
            <span className={styles.infoTitle}>{item.title}</span>
            {/* <span className={styles.desc}>Category / Subcategory</span> */}
            {item.subCategory
              ? (
                <span className={styles.desc}>{`${item.category?.name}/${item.subCategory?.name}`}</span>
              )
              : (
                <span className={styles.desc}>{`${item.category?.name}`}</span>
              )}
          </div>
          <span className={styles.desc}>
            {formatDate(item.createdAt)}
          </span>
        </div>
      </div>
    ));
  };

  const items: TabsProps['items'] = products?.map((product) => ({
    key: product.id,
    label: product.name,
    children: renderTabItems(product.id),
  }));

  const activeProduct = products?.find((p) => p.id === activeTab);

  return (
    <div className={styles.wrapper}>
      <div className={styles.title} style={loading || error ? { marginTop: 50 } : {}}>
        <h2>Top articles</h2>
      </div>
      <div>
        <Tabs
          size="large"
          style={{ fontSize: 16, marginBottom: 10 }}
          activeKey={activeTab}
          items={items}
          onChange={onChange}
        />
        {activeProduct && (
          // eslint-disable-next-line max-len
          <NavLink to={`/product/${activeProduct.id}/${encodeURIComponent(activeProduct.name)}`} className={styles.link}>
            View
            {' '}
            {activeProduct.name}
            {' '}
            categories
          </NavLink>
        )}
      </div>
    </div>
  );
}
